import React from 'react';
import { Box, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { SECTION_GAP } from '../../theme/tokens';

/**
 * FilterBar — search input + filter controls row.
 *
 * The search input here is CONTROLLED and un-debounced by design: the debounce
 * belongs in the fetching hook (`useDebounce(query, 400)`), matching the existing
 * useItemSearch/useDealerSearch pattern. Debouncing here too would stack two
 * delays. Any search hitting a quota-protected backend must debounce >=400ms
 * (docs/UI_STANDARDS.md §3).
 *
 * Props
 * - searchValue / onSearchChange: controlled search text (receives the raw string)
 * - children: filter controls rendered beside the search box
 * - actions: right-aligned controls (buttons)
 */
const FilterBar = ({
    searchValue = '',
    onSearchChange,
    searchPlaceholder = 'Search…',
    children,
    actions,
    sx = {},
}) => (
    <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: SECTION_GAP, ...sx }}
    >
        {onSearchChange && (
            <TextField
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                size="small"
                fullWidth={false}
                sx={{ minWidth: { xs: '100%', md: 320 } }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    ),
                    endAdornment: searchValue ? (
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={() => onSearchChange('')} aria-label="clear search">
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                }}
            />
        )}

        {children && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {children}
            </Stack>
        )}

        {actions && (
            <Box sx={{ ml: { md: 'auto' }, display: 'flex', gap: 1, alignItems: 'center' }}>{actions}</Box>
        )}
    </Stack>
);

export default FilterBar;
